import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import './App.css';
import GlobalStyle from './styles/globalStyles';
import Login from './Components/Login';
import EmployeeRegistration from './Components/EmployeeRegistration';
import KioskAttendance from './Components/KioskAttendance';
import DeviceRegistration from './Components/DeviceRegistration';
import EmployeeManagement from './Components/EmployeeManagement';
import AttendanceReport from './Components/AttendanceReport';
import FaceEnrollment from './Components/FaceEnrollment';
import DeviceIdentifier from './Components/DeviceIdentifier';
import DailyAttendance from './Components/DailyAttendance';
import SpoofingReports from './Components/SpoofingReports';
import Sidebar from './Components/Sidebar';
import ShiftManagement from './Components/ShiftManagement';
import RosterReport from './Components/RosterReport';
import UserRegistration from './Components/UserRegistration';
import UserManagement from './Components/UserManagement';
import RosterAttendanceReport from './Components/RosterAttendanceReport';
import RegisteredDevices from './Components/RegisteredDevices';
import EmployeeDashboard from './Components/EmployeeDashboard';
import LeaveRequest from './Components/LeaveRequest';
import LeaveApprovals from './Components/LeaveApprovals';
import MyAttendanceReport from './Components/MyAttendanceReport';
import MyLeaves from './Components/MyLeaves';
import AdminLeaveHistory from './Components/AdminLeaveHistory';
import { useLocation } from 'react-router-dom';



// Layout with Sidebar
const AppLayout = styled.div`
  display: flex;
  min-height: 100vh;
  width: 100vw;
  max-width: 100vw;
  overflow-x: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const MainContent = styled.main`
  flex: 1;
  min-width: 0;
  width: ${props => props.$noSidebar ? '100%' : (props.$isCollapsed ? 'calc(100vw - 80px)' : 'calc(100vw - 280px)')};
  max-width: ${props => props.$noSidebar ? '100%' : (props.$isCollapsed ? 'calc(100vw - 80px)' : 'calc(100vw - 280px)')};
  margin-left: ${props => props.$noSidebar ? '0' : (props.$isCollapsed ? '80px' : '280px')};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-x: hidden;

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    max-width: 100vw;
  }
`;

const ContentWrapper = styled.div`
  padding: 0;
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow-x: hidden;
`;

// Protected Routes Component (Requires Login)
function ProtectedLayout({ children }) {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const noSidebar = location.pathname === '/webcam';

  // Authentication check
  const token = localStorage.getItem("access_token");

  if (!token) {
    // Redirect to login if not authenticated
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return (
    <AppLayout>
      <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
      <MainContent $noSidebar={noSidebar} $isCollapsed={isCollapsed}>
        <ContentWrapper $noSidebar={noSidebar}>
          {children}
        </ContentWrapper>
      </MainContent>
    </AppLayout>
  );
}

// Public Routes Component (Redirects to dashboard if already logged in)
function PublicRoute({ children }) {
  const token = localStorage.getItem("access_token");

  if (token) {
    // Redirect to dashboard if already authenticated
    const role = localStorage.getItem("role");
    return <Navigate to={role === "Admin" ? "/HRAction" : "/my-dashboard"} replace />;
  }

  return children;
}

function App() {
  // Removed localStorage.clear() on mount to prevent automatic logouts on refresh

  return (
    // In hr_frontend/src/App.js
    <Router>

      <GlobalStyle />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } />

        {/* Protected Routes with Conditional Sidebar */}
        <Route path="/webcam" element={
          <ProtectedLayout>
            <KioskAttendance />
          </ProtectedLayout>
        } />

        <Route path="/register" element={
          <ProtectedLayout>
            <EmployeeRegistration />
          </ProtectedLayout>
        } />

        <Route path="/Hrregister" element={
          <ProtectedLayout>
            <DeviceRegistration />
          </ProtectedLayout>
        } />
        <Route path="/Finger" element={
          <ProtectedLayout>
            <DeviceIdentifier />
          </ProtectedLayout>
        } />
        <Route path="/HRAction" element={
          <ProtectedLayout>
            <EmployeeManagement />
          </ProtectedLayout>
        } />
        <Route path="/hraction" element={
          <ProtectedLayout>
            <EmployeeManagement />
          </ProtectedLayout>
        } />

        <Route path="/AttendanceReport" element={
          <ProtectedLayout>
            <AttendanceReport />
          </ProtectedLayout>
        } />

        <Route path="/daily-attendance" element={
          <ProtectedLayout>
            <DailyAttendance />
          </ProtectedLayout>
        } />

        <Route path="/Faceencoding" element={
          <ProtectedLayout>
            <FaceEnrollment />
          </ProtectedLayout>
        } />

        <Route path="/spoofing-attempts" element={
          <SpoofingReports />

        } />


        <Route path="/shifts" element={
          <ProtectedLayout>
            <ShiftManagement />
          </ProtectedLayout>
        } />

        <Route path="/roster-report" element={
          <ProtectedLayout>
            <RosterReport />
          </ProtectedLayout>
        } />

        <Route path="/user-register" element={
          <ProtectedLayout>
            <UserRegistration />
          </ProtectedLayout>
        } />

        <Route path="/roster-attendance-report" element={
          <ProtectedLayout>
            <RosterAttendanceReport />
          </ProtectedLayout>
        } />

        <Route path="/user-management" element={
          <ProtectedLayout>
            <UserManagement />
          </ProtectedLayout>
        } />

        <Route path="/registered-devices" element={
          <ProtectedLayout>
            <RegisteredDevices />
          </ProtectedLayout>
        } />
        
        {/* Employee Features */}
        <Route path="/my-dashboard" element={
          <ProtectedLayout>
            <EmployeeDashboard />
          </ProtectedLayout>
        } />
        
        <Route path="/leave-request" element={
          <ProtectedLayout>
            <LeaveRequest />
          </ProtectedLayout>
        } />
        <Route path="/my-leaves" element={
          <ProtectedLayout>
            <MyLeaves />
          </ProtectedLayout>
        } />
        <Route path="/leave-approvals" element={
          <ProtectedLayout>
            <LeaveApprovals />
          </ProtectedLayout>
        } />
        <Route path="/leave-history" element={
          <ProtectedLayout>
            <AdminLeaveHistory />
          </ProtectedLayout>
        } />
        <Route path="/my-attendance" element={
          <ProtectedLayout>
            <MyAttendanceReport />
          </ProtectedLayout>
        } />


        {/* 404 Route */}
        <Route path="*" element={
          <ProtectedLayout>
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <h1 style={{ fontSize: '48px', marginBottom: '16px' }}>404</h1>
              <p style={{ color: 'var(--muted)' }}>Page Not Found</p>
            </div>
          </ProtectedLayout>
        } />
      </Routes>
    </Router>
  );
}

export default App;
